/**
 * The account's capture tier (Settings). Every AI bulb carries its own tier now (`bulbTier.ts`),
 * so this is no longer what the bulbs run at: it is the tier for things that arrive without a
 * bulb to choose one — captures from the watch app or the CLI — and the tier a bulb shows until it has been picked on.
 * It lives here rather than on any one card because it belongs to none of them (#82).
 *
 * Not drawn on a server with no AI to ask (`aiConfigured.ts`): there is nothing for the tier to
 * apply to, and the bulbs it seeds are gone too.
 */

import { useState } from "react";
import { useAiConfigured } from "../aiConfigured";
import { ApiError, type CaptureTier } from "../api";
import { useCaptureTier } from "../captureTierContext";
import { Lightbulb } from "./icons";

const OPTIONS: { tier: CaptureTier; label: string; blurb: string }[] = [
  { tier: "off", label: "Off", blurb: "Nothing is processed on its own. New captures wait in your Inbox for you." },
  { tier: "dim", label: "Dim", blurb: "Fast, always available, rougher" },
  { tier: "on", label: "On", blurb: "The default once something's configured" },
  { tier: "bright", label: "Bright", blurb: "Best. Slowest or costliest, worth it when it matters" },
];

export function CaptureTierCard() {
  const { tier, setTier } = useCaptureTier();
  const [error, setError] = useState<string | null>(null);
  const configured = useAiConfigured();

  async function pick(next: CaptureTier) {
    setError(null);
    try {
      await setTier(next);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setError(err.message);
    }
  }

  if (!configured) return null;

  return (
    <section className="panel">
      <header>
        <h2>Capture tier</h2>
        <span className="muted small">Your account</span>
      </header>
      <p className="muted small" style={{ margin: "0 0 0.6rem" }}>
        How much effort the AI puts into anything that arrives without a lightbulb of its own,
        such as a capture sent from the watch app or the command line. Each lightbulb in the app
        keeps its own tier, so this doesn't change what they run at; it's only where a new
        lightbulb starts.
      </p>
      <div role="radiogroup" aria-label="Capture tier" style={{ display: "grid", gap: "0.35rem" }}>
        {OPTIONS.map((option) => (
          <label key={option.tier} className="setting-checkbox-row">
            <input
              type="radio"
              name="capture-tier"
              checked={tier === option.tier}
              onChange={() => void pick(option.tier)}
            />
            <Lightbulb level={option.tier} size={14} />
            <span>
              <strong>{option.label}</strong>
              <span className="muted small"> - {option.blurb}</span>
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p className="error small" role="alert" style={{ margin: "0.5rem 0 0" }}>
          {error}
        </p>
      )}
    </section>
  );
}
